import type { SceneId } from "@/scenes/sceneId";
import { logger } from "@/utils/logger";

import type { GameScene, SceneContext, SceneFactory, SceneRegistry } from "./types";

/**
 * Cycle de vie des scènes : chargement, transition, destruction.
 *
 * Une seule scène est vivante à la fois. La précédente n'est détruite qu'une
 * fois la nouvelle construite, pour ne jamais laisser le moteur sans scène à
 * rendre pendant un chargement asynchrone.
 */
export class SceneManager {
  private readonly registry: SceneRegistry;
  private readonly context: SceneContext;

  private currentScene: GameScene | null = null;
  private currentSceneId: SceneId | null = null;

  /**
   * Incrémenté à chaque `load`. Si un second chargement démarre avant la fin du
   * premier, l'ancien se voit distancé et jette sa scène plutôt que d'écraser
   * la plus récente.
   */
  private loadGeneration = 0;
  private disposed = false;

  constructor(registry: SceneRegistry, context: SceneContext) {
    this.registry = registry;
    this.context = context;
  }

  get current(): GameScene | null {
    return this.currentScene;
  }

  get currentId(): SceneId | null {
    return this.currentSceneId;
  }

  async load(id: SceneId): Promise<void> {
    if (this.disposed) return;

    const factory: SceneFactory = this.registry[id];
    const generation = ++this.loadGeneration;

    logger.info(`Chargement de la scène « ${id} »…`);
    const started = performance.now();

    const nextScene = await factory(this.context);

    // Un chargement plus récent (ou un dispose) est passé entre-temps :
    // la scène qu'on vient de construire n'a plus lieu d'être.
    if (generation !== this.loadGeneration || this.disposed) {
      logger.warn(`Scène « ${id} » abandonnée (transition plus récente).`);
      nextScene.dispose();
      return;
    }

    this.currentScene?.dispose();
    this.currentScene = nextScene;
    this.currentSceneId = id;

    logger.info(`Scène « ${id} » prête en ${Math.round(performance.now() - started)} ms.`);
  }

  dispose(): void {
    this.disposed = true;
    // Invalide tout chargement encore en vol.
    this.loadGeneration++;
    this.currentScene?.dispose();
    this.currentScene = null;
    this.currentSceneId = null;
  }
}
